"use client";

import type { LeadFormDeliveryContext } from "@homeinstead/forms/types";
import type { ReactNode } from "react";

import { createContext, useContext, useMemo } from "react";

/** Known page zone values. Accepts any string to allow future zones. */
export type LeadFormPageZone = "sitebuilder" | "ppc" | (string & {});

interface LeadFormAnalyticsConfig {
  /** Delivery context configured on the lead form document. */
  deliveryContext?: LeadFormDeliveryContext | null;
  /**
   * Franchise / office id for this page. Used for `is_corporate` when
   * `deliveryContext` is unset (pre-backfill forms): `franchiseId === 0` is
   * national/corporate.
   */
  franchiseId: number;
  /** Segment page_zone value (e.g. "sitebuilder"). */
  pageZone: LeadFormPageZone;
  /** The Sanity careLeadForm versionId — sent as form_version_id in events. */
  versionId: string;
}

const LeadFormAnalyticsContext = createContext<LeadFormAnalyticsConfig | null>(
  null,
);

export function LeadFormAnalyticsProvider({
  children,
  deliveryContext,
  franchiseId,
  pageZone,
  versionId,
}: LeadFormAnalyticsConfig & { children: ReactNode }) {
  const value = useMemo(
    () => ({ deliveryContext, franchiseId, pageZone, versionId }),
    [deliveryContext, franchiseId, pageZone, versionId],
  );

  return (
    <LeadFormAnalyticsContext.Provider value={value}>
      {children}
    </LeadFormAnalyticsContext.Provider>
  );
}

/**
 * Returns the Lead Form analytics config from context, or null if no provider is present.
 * Components should gracefully skip tracking when this returns null.
 */
export function useLeadFormAnalytics(): LeadFormAnalyticsConfig | null {
  return useContext(LeadFormAnalyticsContext);
}
